import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { 
  Calculator, Calendar, TrendingUp, TrendingDown, AlertTriangle, Target, 
  DollarSign, BarChart3, PieChart, LineChart, Zap, RefreshCw, Download,
  Plus, Minus, PlayCircle, StopCircle, Settings, Bell
} from 'lucide-react';
import AIInsightsChatbot from '../components/Dashboard/AIInsightsChatbot';

const BudgetSimulator = () => {
  const [totalBudget, setTotalBudget] = useState(18000000);
  const [simulationPeriod, setSimulationPeriod] = useState('Q2-2025');
  const [selectedScenario, setSelectedScenario] = useState('baseline');
  const [isSimulating, setIsSimulating] = useState(false);

  // Channel allocation state
  const [channelAllocation, setChannelAllocation] = useState({
    'Digital': 35,
    'TV': 30,
    'Outdoor': 20,
    'Radio': 10,
    'Print': 5
  });

  // Event planning state
  const [events, setEvents] = useState([
    {
      id: 1,
      name: 'Ramadan Campaign',
      type: 'seasonal',
      startDate: '2025-03-10',
      endDate: '2025-04-09',
      budget: 2500000,
      expectedLift: 18.5,
      channels: ['Digital', 'TV', 'Outdoor'],
      status: 'planned'
    },
    {
      id: 2,
      name: 'UAE National Day',
      type: 'national',
      startDate: '2025-12-01',
      endDate: '2025-12-02',
      budget: 1200000,
      expectedLift: 12.3,
      channels: ['Outdoor', 'Digital'],
      status: 'scheduled'
    },
    {
      id: 3,
      name: 'Summer Flash Sale',
      type: 'promotional',
      startDate: '2025-07-15',
      endDate: '2025-07-31',
      budget: 800000,
      expectedLift: 25.4,
      channels: ['Digital'],
      status: 'draft'
    }
  ]);

  // Simulation scenarios
  const scenarios = {
    baseline: {
      name: 'Current Allocation',
      description: 'Maintain existing channel mix',
      expectedROI: 3.2,
      riskLevel: 'Low'
    },
    aggressive_digital: {
      name: 'Digital Focus',
      description: 'Shift 15% more budget to digital channels',
      expectedROI: 3.8,
      riskLevel: 'Medium'
    },
    seasonal_boost: {
      name: 'Event-Driven',
      description: 'Optimize for seasonal campaigns',
      expectedROI: 4.1,
      riskLevel: 'Medium-High'
    },
    balanced_growth: {
      name: 'Balanced Growth',
      description: 'Diversified approach across all channels',
      expectedROI: 3.5,
      riskLevel: 'Low-Medium'
    }
  };

  // Forecast results
  const forecastResults = {
    incrementalLift: {
      sales: 2.4,
      conversions: 18.7,
      revenue: 5200000
    },
    attribution: {
      'Digital': 42.5,
      'TV': 28.3,
      'Outdoor': 18.2,
      'Radio': 7.8,
      'Print': 3.2
    },
    benchmarkComparison: {
      industryAverage: 3.1,
      topQuartile: 4.2,
      currentPosition: 'Above Average',
      improvement: '+12.9%'
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleChannelChange = (channel, value) => {
    setChannelAllocation(prev => ({
      ...prev,
      [channel]: value
    }));
  };

  const runSimulation = () => {
    setIsSimulating(true);
    // Simulate API call delay
    setTimeout(() => {
      setIsSimulating(false);
    }, 3000);
  };

  const getTotalAllocation = () => {
    return Object.values(channelAllocation).reduce((sum, val) => sum + val, 0);
  };

  const getChannelBudget = (channel) => {
    return (totalBudget * channelAllocation[channel]) / 100;
  };

  const getRiskBadgeColor = (risk) => {
    switch(risk) {
      case 'Low': return 'bg-green-100 text-green-800';
      case 'Low-Medium': return 'bg-blue-100 text-blue-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Medium-High': return 'bg-orange-100 text-orange-800';
      case 'High': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
            Budget Simulator & Event Planner
          </h1>
          <p className="text-gray-600">Advanced forecasting, scenario planning, and event optimization for MENA campaigns</p>
        </div>

        {/* Controls */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Settings className="h-5 w-5 mr-2 text-blue-600" />
              Simulation Configuration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="number"
                    value={totalBudget}
                    onChange={(e) => setTotalBudget(Number(e.target.value))}
                    className="pl-10"
                    placeholder="Total Budget"
                    aria-label="Total Budget"
                  />
                </div>
              </div>
              
              <Select value={simulationPeriod} onValueChange={setSimulationPeriod}>
                <SelectTrigger>
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Q1-2025">Q1 2025</SelectItem>
                  <SelectItem value="Q2-2025">Q2 2025</SelectItem>
                  <SelectItem value="Q3-2025">Q3 2025</SelectItem>
                  <SelectItem value="Q4-2025">Q4 2025</SelectItem>
                  <SelectItem value="FY-2025">Full Year 2025</SelectItem>
                </SelectContent>
              </Select>

              <Select value={selectedScenario} onValueChange={setSelectedScenario}>
                <SelectTrigger>
                  <SelectValue placeholder="Select scenario" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(scenarios).map(([key, scenario]) => (
                    <SelectItem key={key} value={key}>{scenario.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                onClick={runSimulation}
                disabled={isSimulating}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                {isSimulating ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <PlayCircle className="h-4 w-4 mr-2" />
                )}
                {isSimulating ? 'Simulating...' : 'Run Simulation'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="simulator" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="simulator">Budget Simulator</TabsTrigger>
            <TabsTrigger value="events">Event Planner</TabsTrigger>
            <TabsTrigger value="forecasts">Forecasts & What-if</TabsTrigger>
            <TabsTrigger value="benchmarks">Benchmarks & Attribution</TabsTrigger>
          </TabsList>

          <TabsContent value="simulator" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Channel Allocation */}
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span className="flex items-center">
                        <PieChart className="h-5 w-5 mr-2 text-blue-600" />
                        Channel Budget Allocation
                      </span>
                      <Badge className={`${getTotalAllocation() === 100 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {getTotalAllocation()}% Allocated
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Object.entries(channelAllocation).map(([channel, percentage]) => (
                        <div key={channel} className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-gray-700">{channel}</span>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-semibold">{percentage}%</span>
                              <span className="text-sm text-gray-500">
                                {formatCurrency(getChannelBudget(channel))}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleChannelChange(channel, Math.max(0, percentage - 5))}
                              className="h-8 w-8 p-0"
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <div className="flex-1 bg-gray-200 rounded-full h-3">
                              <div
                                className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-300"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleChannelChange(channel, Math.min(100, percentage + 5))}
                              className="h-8 w-8 p-0"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Scenario Analysis */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Target className="h-5 w-5 mr-2 text-green-600" />
                    Scenario Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(scenarios).map(([key, scenario]) => (
                      <div key={key} className={`p-3 rounded-lg border ${selectedScenario === key ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-semibold text-gray-900">{scenario.name}</h4>
                          <Badge className={getRiskBadgeColor(scenario.riskLevel)}>
                            {scenario.riskLevel}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{scenario.description}</p>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">Expected ROI:</span>
                          <span className="text-lg font-bold text-green-600">{scenario.expectedROI}x</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Incremental Lift Analysis */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2 text-green-600" />
                  Incremental Lift Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 mb-1">+{forecastResults.incrementalLift.sales}M</div>
                    <div className="text-sm text-gray-600">Incremental Sales</div>
                    <div className="text-xs text-green-600 mt-1">vs baseline scenario</div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600 mb-1">+{forecastResults.incrementalLift.conversions}%</div>
                    <div className="text-sm text-gray-600">Conversion Lift</div>
                    <div className="text-xs text-blue-600 mt-1">cross-channel impact</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600 mb-1">{formatCurrency(forecastResults.incrementalLift.revenue)}</div>
                    <div className="text-sm text-gray-600">Revenue Impact</div>
                    <div className="text-xs text-purple-600 mt-1">attributable to media</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="events" className="space-y-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Campaign Events & Seasonality</h3>
              <Button className="bg-gradient-to-r from-blue-600 to-purple-600">
                <Plus className="h-4 w-4 mr-2" />
                Add New Event
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map((event) => (
                <Card key={event.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{event.name}</CardTitle>
                        <Badge className="bg-blue-100 text-blue-800 text-xs mt-1">{event.type}</Badge>
                      </div>
                      <Badge className={`${
                        event.status === 'planned' ? 'bg-green-100 text-green-800' :
                        event.status === 'scheduled' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {event.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="h-4 w-4 mr-2" />
                        {event.startDate} to {event.endDate}
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Budget:</span>
                        <span className="font-semibold">{formatCurrency(event.budget)}</span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Expected Lift:</span>
                        <span className="font-semibold text-green-600">+{event.expectedLift}%</span>
                      </div>

                      <div className="space-y-1">
                        <span className="text-sm text-gray-600">Channels:</span>
                        <div className="flex flex-wrap gap-1">
                          {event.channels.map((channel, index) => (
                            <Badge key={index} className="bg-purple-100 text-purple-800 text-xs">
                              {channel}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div className="pt-2 border-t">
                        <Button variant="outline" size="sm" className="w-full">
                          <Settings className="h-4 w-4 mr-2" />
                          Configure Event
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="forecasts" className="space-y-6">
            {/* What-if Scenarios */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Calculator className="h-5 w-5 mr-2 text-blue-600" />
                  What-if Scenario Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900">Scenario Builder</h4>
                    
                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-medium">Digital Budget Increase</span>
                        <Badge className="bg-blue-100 text-blue-800">+25%</Badge>
                      </div>
                      <div className="text-sm text-gray-600 mb-2">
                        Impact: Move $1.5M from TV to Digital channels
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">ROI Change:</span>
                          <span className="ml-2 font-semibold text-green-600">+0.6x</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Revenue Lift:</span>
                          <span className="ml-2 font-semibold text-green-600">+$2.1M</span>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-medium">Seasonal Budget Boost</span>
                        <Badge className="bg-orange-100 text-orange-800">Ramadan</Badge>
                      </div>
                      <div className="text-sm text-gray-600 mb-2">
                        Impact: 40% increase during Ramadan period
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Conversion Lift:</span>
                          <span className="ml-2 font-semibold text-green-600">+28%</span>
                        </div>
                        <div>
                          <span className="text-gray-600">ROAS:</span>
                          <span className="ml-2 font-semibold text-green-600">4.8x</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900">Trend Forecasting</h4>
                    
                    <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
                      <div className="flex items-center mb-3">
                        <LineChart className="h-5 w-5 mr-2 text-blue-600" />
                        <span className="font-medium">6-Month Projection</span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Q2 2025 Revenue:</span>
                          <span className="font-semibold">$24.8M</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Q3 2025 Revenue:</span>
                          <span className="font-semibold">$21.2M</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Confidence Interval:</span>
                          <span className="font-semibold text-green-600">±8.5%</span>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg">
                      <div className="flex items-center mb-3">
                        <AlertTriangle className="h-5 w-5 mr-2 text-yellow-600" />
                        <span className="font-medium">Saturation Analysis</span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Digital Saturation:</span>
                          <span className="font-semibold text-yellow-600">72%</span>
                        </div>
                        <div className="flex justify-between">
                          <span>TV Saturation:</span>
                          <span className="font-semibold text-green-600">45%</span>
                        </div>
                        <div className="text-xs text-gray-600 mt-2">
                          Diminishing returns detected in digital channels
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Export & Reporting */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Download className="h-5 w-5 mr-2 text-green-600" />
                  Data Export & BI Integration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Button variant="outline" className="flex items-center">
                    <Download className="h-4 w-4 mr-2" />
                    Export to Excel
                  </Button>
                  <Button variant="outline" className="flex items-center">
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Tableau Dashboard
                  </Button>
                  <Button variant="outline" className="flex items-center">
                    <Zap className="h-4 w-4 mr-2" />
                    PowerBI Integration
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="benchmarks" className="space-y-6">
            {/* Attribution Analysis */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <PieChart className="h-5 w-5 mr-2 text-purple-600" />
                  Media Attribution Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4">Attribution by Channel</h4>
                    <div className="space-y-3">
                      {Object.entries(forecastResults.attribution).map(([channel, percentage]) => (
                        <div key={channel} className="flex items-center justify-between">
                          <span className="font-medium text-gray-700">{channel}</span>
                          <div className="flex items-center space-x-2">
                            <div className="w-20 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                            <span className="text-sm font-semibold w-12 text-right">{percentage}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4">Cross-Channel Impact</h4>
                    <div className="space-y-3">
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <div className="font-medium text-blue-900">Digital → TV Synergy</div>
                        <div className="text-sm text-blue-700">+15% lift when combined</div>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg">
                        <div className="font-medium text-green-900">Outdoor → Digital</div>
                        <div className="text-sm text-green-700">+22% search volume increase</div>
                      </div>
                      <div className="p-3 bg-purple-50 rounded-lg">
                        <div className="font-medium text-purple-900">TV → All Channels</div>
                        <div className="text-sm text-purple-700">+8% brand awareness halo</div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Benchmark Comparison */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2 text-green-600" />
                  Industry Benchmarks & Competitive Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 mb-1">
                      {forecastResults.benchmarkComparison.improvement}
                    </div>
                    <div className="text-sm text-gray-600">vs Industry Average</div>
                    <div className="text-xs text-green-600 mt-1">
                      {forecastResults.benchmarkComparison.currentPosition}
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Your ROI:</span>
                      <span className="font-bold text-blue-600">3.2x</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Industry Avg:</span>
                      <span className="font-semibold">{forecastResults.benchmarkComparison.industryAverage}x</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Top Quartile:</span>
                      <span className="font-semibold text-green-600">{forecastResults.benchmarkComparison.topQuartile}x</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-900">Optimization Opportunities</h4>
                    <div className="text-sm space-y-1">
                      <div className="text-green-600">• Digital efficiency +18%</div>
                      <div className="text-blue-600">• Cross-channel synergy +12%</div>
                      <div className="text-purple-600">• Seasonal timing +25%</div>
                      <div className="text-orange-600">• Creative optimization +8%</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* AI Insights Chatbot */}
      <AIInsightsChatbot />
    </div>
  );
};

export default BudgetSimulator;